// Overlap assignment for calendar events: sets overlapping / overlappingCnt /
// overlappingIdx on the passed event objects (the grids read these fields for
// the 50%-width two-column layout, keyed on overlappingIdx % 2).
//
// Produces the exact values a fresh run of the legacy O(n²) loop produced:
// partners share a calendarId, intersect strictly (start < otherEnd &&
// end > otherStart) and have different ids (recurring occurrences share their
// series id and therefore never pair; two id-less events never pair). Each
// event's idx is max(partner idx assigned so far) + 1, walked in original
// array order. Unlike the legacy loop this is deterministic and idempotent:
// every run resets the fields first, so values never grow between runs.
export function assignOverlaps(events = []) {
    const entries = [];
    for (let i = 0; i < events.length; i += 1) {
        const ev = events[i];
        ev.overlapping = false;
        ev.overlappingCnt = 0;
        ev.overlappingIdx = 0;
        entries.push({
            ev,
            arrayIdx: i,
            startEpoch: new Date(ev.start).getTime(),
            endEpoch: new Date(ev.end).getTime(),
            partners: [],
        });
    }

    // Overlaps only exist within the same calendar. Events with invalid dates
    // are reset above but can never pair (NaN comparisons are always false).
    const byCalendar = new Map();
    for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        if (Number.isNaN(entry.startEpoch) || Number.isNaN(entry.endEpoch)) {
            continue;
        }
        let group = byCalendar.get(entry.ev.calendarId);
        if (!group) {
            group = [];
            byCalendar.set(entry.ev.calendarId, group);
        }
        group.push(entry);
    }

    byCalendar.forEach(group => {
        group.sort((a, b) => (a.startEpoch - b.startEpoch) || (a.arrayIdx - b.arrayIdx));
        for (let i = 0; i < group.length; i += 1) {
            const a = group[i];
            // Sorted by start, so no event starting at/after a's end can overlap a.
            for (let j = i + 1; j < group.length && group[j].startEpoch < a.endEpoch; j += 1) {
                const b = group[j];
                if (a.startEpoch < b.endEpoch && a.endEpoch > b.startEpoch && a.ev.id !== b.ev.id) {
                    a.partners.push(b);
                    b.partners.push(a);
                }
            }
        }
    });

    // Assign in original array order so idx values match a fresh legacy run:
    // partners earlier in the array contribute their new idx, later ones 0.
    for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];
        if (entry.partners.length > 0) {
            let maxIdx = 0;
            for (let p = 0; p < entry.partners.length; p += 1) {
                const partnerIdx = entry.partners[p].ev.overlappingIdx || 0;
                if (partnerIdx > maxIdx) {
                    maxIdx = partnerIdx;
                }
            }
            entry.ev.overlapping = true;
            entry.ev.overlappingCnt = entry.partners.length;
            entry.ev.overlappingIdx = maxIdx + 1;
        }
    }
}
